import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";
import { UserContext } from "../../contexts";

/**
 * Owner Admin → Payments: the store's OWN Stripe account for "Stripe Link".
 *
 * The merchant pastes their Stripe secret (or restricted) key. From then on a
 * Stripe Link on an order is a Stripe-hosted checkout link (buy.stripe.com) on
 * THEIR account — the customer pays Stripe directly and the money lands with
 * the merchant, never with DAO Watches / Retail Circle. The backend validates
 * the key by registering a webhook on their account, so a payment is recorded
 * on the order automatically.
 *
 *   GET    /api/stores/{store}/stripe/own-account
 *   PUT    /api/stores/{store}/stripe/own-account  { secret_key }
 *   DELETE /api/stores/{store}/stripe/own-account
 *
 * The key never comes back from the API — only its last 4 characters.
 */
export function StripeOwnAccountPanel({ user }) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const { activeStoreId } = useContext(UserContext);
  // Same rule as StripeConnectPanel: manage the ACTIVE store.
  const storeIdM = activeStoreId ?? user?.store_id_m;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [secretKey, setSecretKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = async () => {
    if (!storeIdM) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/stores/${storeIdM}/stripe/own-account`);
      setStatus(data);
    } catch (err) {
      showApiError(err, "load your Stripe account");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeIdM]);

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`/api/stores/${storeIdM}/stripe/own-account`, {
        secret_key: secretKey.trim(),
      });
      setStatus(data);
      setSecretKey("");
      alert.show("Stripe account saved — Stripe Links will now be paid into this account.");
    } catch (err) {
      showApiError(err, "save your Stripe key");
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async () => {
    if (
      !window.confirm(
        "Remove this Stripe account? New Stripe Links can't be created until you add a key again."
      )
    ) {
      return;
    }
    setRemoving(true);
    try {
      const { data } = await axios.delete(`/api/stores/${storeIdM}/stripe/own-account`);
      setStatus(data);
      alert.show("Stripe account removed.");
    } catch (err) {
      showApiError(err, "remove your Stripe account");
    } finally {
      setRemoving(false);
    }
  };

  if (!storeIdM) {
    return (
      <p className="ma-section__empty">
        No store is associated with your account, so Stripe can&apos;t be configured here.
      </p>
    );
  }
  if (loading) return <p className="ma-section__empty">Loading…</p>;
  if (!status) {
    return (
      <div className="ma-wf-creds">
        <div className="ma-wf-creds__status">
          <span className="ma-wf-creds__off">
            Couldn&apos;t load Stripe for this store. Pick a specific store in the store
            switcher (Stripe is set up per store), then reload.
          </span>
        </div>
        <button type="button" className="rc-btn rc-btn--small" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  if (status.is_default) {
    // DAO's default store: shown the same way as a merchant's account, but
    // READ-ONLY — it describes the platform key the existing DAO links already
    // use. No key field, no replace/remove, nothing to reconnect.
    return (
      <div className="ma-wf-creds">
        <div className="ma-wf-creds__status">
          {status.connected ? (
            <span className="ma-wf-creds__ok">
              ✓ Connected
              {status.account_name ? ` — ${status.account_name}` : ""}
              {status.account_id ? ` (${status.account_id})` : ""}
              {status.key_last4 ? ` · key ending ${status.key_last4}` : ""}
              {!status.livemode ? " · TEST MODE" : ""}
            </span>
          ) : (
            <span className="ma-wf-creds__off">Platform Stripe key is not configured.</span>
          )}
        </div>
        <div className="ma-section__hint">
          This store uses the platform Stripe account, managed by Retail Circle — nothing to
          set up here.
          {status.payment_page ? (
            <>
              {" "}Customers pay on <strong>{status.payment_page.replace(/^https?:\/\//, "")}/pay</strong>.
            </>
          ) : null}
        </div>
      </div>
    );
  }

  const keyLooksValid = /^(sk|rk)_(live|test)_\S{10,}$/.test(secretKey.trim());

  return (
    <div className="ma-wf-creds">
      <div className="ma-wf-creds__status">
        {status.connected ? (
          <span className="ma-wf-creds__ok">
            ✓ Connected
            {status.account_name ? ` — ${status.account_name}` : ""}
            {status.account_id ? ` (${status.account_id})` : ""}
            {status.key_last4 ? ` · key ending ${status.key_last4}` : ""}
            {!status.livemode ? " · TEST MODE" : ""}
          </span>
        ) : (
          <span className="ma-wf-creds__off">Not set up</span>
        )}
      </div>

      <div className="ma-section__hint">
        <strong>What this does:</strong> when you choose <strong>Stripe Link</strong> on an
        order, your client gets a secure Stripe checkout link and the money goes straight
        into <em>your own</em> Stripe account. The payment is marked paid on the order
        automatically.
        <br />1. In your Stripe Dashboard open <strong>Developers → API keys</strong>.
        <br />2. Copy your <strong>Secret key</strong> (sk_live_…), or create a restricted
        key with write access to Payment Links, Prices, Products and Webhook Endpoints and
        read access to PaymentIntents and Checkout Sessions.
        <br />3. Paste it below and save. We store it encrypted and never show it again.
      </div>

      <div className="ma-wf-creds__key">
        <input
          type="password"
          value={secretKey}
          autoComplete="off"
          placeholder={
            status.connected ? "•••••••• stored — paste a new key to replace" : "sk_live_…"
          }
          onChange={(e) => setSecretKey(e.target.value)}
        />
        <button
          type="button"
          className="rc-btn rc-btn--small"
          onClick={onSave}
          disabled={saving || !keyLooksValid}
        >
          {saving ? "Checking…" : status.connected ? "Replace key" : "Save key"}
        </button>
        {status.connected && (
          <button
            type="button"
            className="rc-btn rc-btn--small rc-btn--ghost"
            onClick={onRemove}
            disabled={removing}
            style={{ marginLeft: 8 }}
          >
            {removing ? "Removing…" : "Remove"}
          </button>
        )}
      </div>
      {status.connected && status.open_links > 0 && (
        <p className="ma-section__hint">
          {status.open_links} unpaid Stripe link(s) are open on this account. The key can be
          replaced or removed once they are paid, revoked or expired.
        </p>
      )}
    </div>
  );
}
